import type { FormEvent, MouseEvent } from 'react'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useApi } from './ApiProvider'
import { useIsTruncated } from '../utils/hooks/useIsTruncated'
import { useTimedToggle } from '../utils/hooks/useTimedToggle'
import { useTranslate } from '../utils/translate'

type AvailabilityNotificationFormProps = {
  productId: string
}

export function AvailabilityNotificationForm({ productId }: Readonly<AvailabilityNotificationFormProps>) {
  const [isConfirmationVisible, triggerConfirmation] = useTimedToggle()
  const [isErrorVisible, triggerError] = useTimedToggle()
  const t = useTranslate('shop', 'components.productComponent.productAvailabilityNotificationForm')
  const csrfToken = useSelector<State, string>((state) => state.getIn(['shop', 'csrfToken']))
  const locale = useSelector<State, string | undefined>((state) => state.getIn(['shop', 'locale']))
  const productsMap = useSelector<State, ImmutableMap | undefined>((state) => state.get('products'))
  const product: ImmutableMap | undefined = productsMap?.find((p: ImmutableMap) => p.get('productId') === productId)
  const rawMerchantText: string = (product?.get('productAvailabilityNotificationLegalText') as string) ?? ''
  const processedMerchantText = rawMerchantText.replace(/\n/g, '<br>')
  const api = useApi()

  const emailInputId = `availability-email-${productId}`
  const checkboxRef = useRef<HTMLInputElement | null>(null)
  const [isTruncated, truncRef] = useIsTruncated<HTMLLabelElement>()
  const [isConsentExpanded, setIsConsentExpanded] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const form = event.currentTarget
    const formData = new FormData(form)
    const email = formData.get('email')

    try {
      await api.post(
        `/api/v2/products/${productId}/availability-notifications`,
        {
          email,
          locale,
        },
        {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        },
      )
      triggerConfirmation()
      form.reset()
    } catch {
      triggerError()
    }
  }

  const handleConsentLabelExpandToggle = (_event: MouseEvent<HTMLButtonElement>) => {
    setIsConsentExpanded((prev) => !prev)
  }

  return (
    <form onSubmit={handleSubmit} className="availability-notification-form">
      {isConfirmationVisible ? (
        <div className="notify-notification notify-notification-success">{t('confirmationMessage')}</div>
      ) : null}
      {isErrorVisible ? (
        <div className="notify-notification notify-notification-failure">{t('errorMessage')}</div>
      ) : null}
      <h4>{t('headline')}</h4>
      <p>{t('description')}</p>
      <label htmlFor={emailInputId}>
        {t('emailField.label')}
        <span aria-hidden="true"> *</span>
      </label>
      <input
        type="email"
        name="email"
        id={emailInputId}
        placeholder={t('emailField.placeholder')}
        required
        aria-required="true"
        autoComplete="email"
      />
      {rawMerchantText && (
        <div className="notify-consent">
          <div className="notify-consent-row">
            <input ref={checkboxRef} type="checkbox" id="notifyConsent" name="notifyConsent" required />
            <label
              htmlFor="notifyConsent"
              className={`notify-consent-label notify-consent-text${isConsentExpanded ? ' notify-consent-expanded' : ''}`}
              aria-label={rawMerchantText}
              data-testid="consent-label"
              ref={truncRef}
            >
              <span dangerouslySetInnerHTML={{ __html: processedMerchantText }} />
            </label>
            {(isTruncated || isConsentExpanded) && (
              <button
                type="button"
                className="notify-consent-caret"
                aria-label={t(isConsentExpanded ? 'collapseDetails.ariaLabel' : 'expandDetails.ariaLabel')}
                onClick={handleConsentLabelExpandToggle}
                data-testid="expand-notify-consent-button"
              />
            )}
          </div>
        </div>
      )}
      <button type="submit">{t('submitButton.label')}</button>
    </form>
  )
}
